import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  BadgeCheck,
  Wrench,
  ScrollText,
  Sparkles,
  Quote,
  ArrowRight,
  Star,
  Plus,
  Minus,
  MapPin,
  Phone,
  Clock,
} from "lucide-react";
import api, { BUSINESS, waLink } from "../lib/api";
import CarCard from "../components/CarCard";
import { toast } from "sonner";

const heroBg =
  "https://images.pexels.com/photos/20131971/pexels-photo-20131971.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

const whyChoose = [
  { icon: ShieldCheck, title: "150+ Point Inspection", body: "Every car undergoes a rigorous multi-point inspection by certified technicians before listing." },
  { icon: BadgeCheck, title: "Verified Ownership", body: "Clear RC, transparent history, original documents — no surprises, ever." },
  { icon: Wrench, title: "Post-Sale Support", body: "Service tie-ups, warranty options and 24/7 roadside support to keep you driving." },
  { icon: ScrollText, title: "Easy Finance", body: "Loan & insurance assistance with leading banks — approval in under 24 hours." },
];

const services = [
  { title: "Buy Used Cars", desc: "Premium curated inventory of certified pre-owned cars in Nagpur.", to: "/inventory" },
  { title: "Sell Your Car", desc: "Get the best market price with same-day quote and instant payment.", to: "/sell" },
  { title: "Car Exchange", desc: "Trade-in your current car for an upgrade with transparent valuation.", to: "/services" },
  { title: "Finance & Insurance", desc: "End-to-end loan and insurance assistance with leading partners.", to: "/services" },
];

const testimonials = [
  { name: "Rohit Sharma", role: "Mercedes C-Class Owner", text: "Bought my dream Mercedes from Faisal Bhai. The transparency, paperwork and condition of the car was top notch. Highly recommended in Nagpur." },
  { name: "Priya Deshmukh", role: "Honda City Buyer", text: "Excellent service! They helped me arrange finance within a day. The car was exactly as described. Very honest dealership." },
  { name: "Aditya Patel", role: "Audi A4 Owner", text: "Faisal Car Bazar has truly redefined premium pre-owned car buying in Nagpur. Their inspection and after-sales support is class-apart." },
  { name: "Sanjay Verma", role: "BMW 3 Series Owner", text: "Smooth and trustworthy experience. Got my BMW at a great price with all RC transfer done by them. Will definitely refer friends." },
];

const faqs = [
  { q: "Are all the cars certified and inspected?", a: "Yes. Every car listed at Faisal Car Bazar passes a 150+ point inspection covering engine, transmission, electricals, body, interiors, tyres, brakes and documentation. We share the full inspection report with you." },
  { q: "Do you provide finance assistance?", a: "Absolutely. We are tied up with leading banks and NBFCs — most loan approvals are processed within 24 hours with attractive interest rates." },
  { q: "Can I exchange my old car?", a: "Yes. Bring your existing car for a free, transparent valuation and exchange it against any car in our inventory." },
  { q: "Is RC transfer handled by you?", a: "We assist with complete RC transfer, insurance, and all post-sale paperwork — you take the keys, we handle the rest." },
  { q: "Do you offer warranty on used cars?", a: "Yes. Most premium pre-owned cars come with an optional 6-12 month engine & transmission warranty." },
];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);
  const [tIdx, setTIdx] = useState(0);

  useEffect(() => {
    api
      .get("/cars", { params: { featured: true, page_size: 6, status: "available" } })
      .then((r) => setFeatured(r.data.items))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const id = setInterval(() => setTIdx((i) => (i + 1) % testimonials.length), 6000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    document.title = "Faisal Car Bazar — Premium Pre-Owned Cars in Nagpur";
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <img
          src={heroBg}
          alt="Premium showroom"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-32 w-full">
          <div className="max-w-3xl fade-up">
            <div className="eyebrow mb-6" data-testid="hero-eyebrow">Nagpur · Since 2009</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#F5F5DC] font-light leading-[1.05] tracking-tight">
              Where Pre-Owned <br />
              feels <em className="text-[#DC143C] not-italic font-medium">absolutely new.</em>
            </h1>

            {/* Trust Badge */}
            <div className="mt-10" data-testid="hero-trust-bar">
              <div className="trust-badge inline-flex flex-col sm:flex-row items-center sm:gap-4 gap-1.5 px-5 sm:px-8 py-3 sm:py-4 rounded-2xl sm:rounded-full text-center">
                <span className="trust-badge-text text-[11px] sm:text-sm md:text-base font-bold uppercase tracking-[0.18em] sm:tracking-[0.22em] leading-none">
                  100% Trusted
                </span>
                <span className="trust-badge-dot hidden sm:inline">•</span>
                <span className="trust-badge-text text-[11px] sm:text-sm md:text-base font-bold uppercase tracking-[0.16em] sm:tracking-[0.22em] leading-tight sm:leading-none">
                  Verified Partner of OLX &amp; CarTrade
                </span>
              </div>
            </div>

            <p className="text-[#A0A0A0] text-base sm:text-lg mt-6 max-w-xl leading-relaxed">
              A hand-picked collection of premium used cars, certified through a 150+ point
              inspection — delivered with the confidence of a brand-new purchase.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/inventory" data-testid="hero-cta-inventory" className="btn-primary">
                Explore Inventory <ArrowRight size={16} />
              </Link>
              <Link to="/sell" data-testid="hero-cta-sell" className="btn-secondary">
                Sell Your Car
              </Link>
            </div>

            <div className="mt-16 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { n: "15+", l: "Years Trust" },
                { n: "1200+", l: "Cars Delivered" },
                { n: "1100+", l: "Happy Customers" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-display text-3xl text-[#D4AF37]">{s.n}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0A0] mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED CARS */}
      <section className="py-24 md:py-32" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="eyebrow mb-4">Curated Collection</div>
              <h2 className="font-display text-4xl sm:text-5xl text-[#F5F5DC] font-light leading-tight">
                Featured automobiles, <br />
                <span className="text-[#DC143C] italic">ready for delivery.</span>
              </h2>
            </div>
            <Link to="/inventory" data-testid="featured-view-all" className="text-sm text-[#D4AF37] hover:text-white tracking-wide flex items-center gap-2">
              View Full Inventory <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.slice(0, 6).map((car) => (
              <CarCard key={car.id} car={car} />
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 md:py-32 bg-[#0E0E0E] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="eyebrow justify-center inline-flex mb-4">Why Choose Us</div>
            <h2 className="font-display text-4xl sm:text-5xl text-[#F5F5DC] font-light max-w-2xl mx-auto leading-tight">
              The dealership that earned <em className="text-[#D4AF37] not-italic">Nagpur's trust.</em>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChoose.map((w) => (
              <div key={w.title} className="premium-card p-7 hover-glow">
                <div className="w-12 h-12 rounded-xl bg-[#DC143C]/10 border border-[#DC143C]/20 flex items-center justify-center mb-5">
                  <w.icon className="text-[#DC143C]" size={22} />
                </div>
                <h3 className="font-display text-xl text-[#F5F5DC] mb-2">{w.title}</h3>
                <p className="text-sm text-[#A0A0A0] leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-12">
            <div className="lg:col-span-7">
              <div className="eyebrow mb-4">Our Services</div>
              <h2 className="font-display text-4xl sm:text-5xl text-[#F5F5DC] font-light leading-tight">
                Beyond buying & selling — <br />
                <span className="text-[#DC143C] italic">we handle everything.</span>
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[#A0A0A0] leading-relaxed">
                From finance and insurance to RC transfer and full vehicle inspection,
                we take care of every detail so your ownership begins effortlessly.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => (
              <Link
                key={s.title}
                to={s.to}
                data-testid={`service-card-${i}`}
                className="premium-card p-7 group flex flex-col"
              >
                <Sparkles className="text-[#D4AF37] mb-5" size={22} />
                <h3 className="font-display text-xl text-[#F5F5DC] mb-2">{s.title}</h3>
                <p className="text-sm text-[#A0A0A0] leading-relaxed mb-5 flex-1">{s.desc}</p>
                <span className="text-xs uppercase tracking-[0.2em] text-[#DC143C] flex items-center gap-2 group-hover:gap-3 transition-all">
                  Learn more <ArrowRight size={12} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 bg-[#0E0E0E] border-y border-white/5">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="eyebrow justify-center inline-flex mb-6">Words from Owners</div>
          <Quote className="mx-auto text-[#DC143C] mb-8" size={40} />
          <p className="font-display text-2xl sm:text-3xl lg:text-4xl text-[#F5F5DC] font-light leading-relaxed italic min-h-[180px]" data-testid="testimonial-quote">
            "{testimonials[tIdx].text}"
          </p>
          <div className="mt-8">
            <div className="text-[#D4AF37] tracking-wide">{testimonials[tIdx].name}</div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#A0A0A0] mt-1">{testimonials[tIdx].role}</div>
            <div className="flex justify-center gap-1 mt-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={14} className="text-[#D4AF37] fill-[#D4AF37]" />
              ))}
            </div>
          </div>
          <div className="flex justify-center gap-2 mt-10">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setTIdx(i)}
                data-testid={`testimonial-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === tIdx ? "w-10 bg-[#DC143C]" : "w-3 bg-white/20"
                }`}
                aria-label={`Testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="eyebrow justify-center inline-flex mb-4">Common Questions</div>
            <h2 className="font-display text-4xl sm:text-5xl text-[#F5F5DC] font-light">
              Everything you need to know.
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="premium-card overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    data-testid={`faq-toggle-${i}`}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  >
                    <span className="font-display text-lg text-[#F5F5DC]">{f.q}</span>
                    <span className="w-9 h-9 rounded-full bg-[#DC143C]/10 border border-[#DC143C]/30 flex items-center justify-center text-[#DC143C] shrink-0">
                      {open ? <Minus size={16} /> : <Plus size={16} />}
                    </span>
                  </button>
                  {open && (
                    <div className="px-6 pb-6 text-[#A0A0A0] leading-relaxed text-sm">{f.a}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA + Contact + Map */}
      <section className="py-24 md:py-32 bg-[#0E0E0E] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="eyebrow mb-4">Visit Showroom</div>
            <h2 className="font-display text-4xl sm:text-5xl text-[#F5F5DC] font-light leading-tight mb-6">
              Step inside the showroom <span className="text-[#DC143C] italic">in Nagpur.</span>
            </h2>
            <div className="space-y-5 text-sm text-[#F5F5DC]/85">
              <div className="flex gap-4">
                <MapPin className="text-[#D4AF37] flex-shrink-0 mt-1" size={18} />
                <div className="leading-relaxed">{BUSINESS.address}</div>
              </div>
              <div className="flex gap-4 items-center">
                <Phone className="text-[#D4AF37]" size={18} />
                <a href={`tel:${BUSINESS.phone}`} className="hover:text-[#D4AF37]">{BUSINESS.phone}</a>
              </div>
              <div className="flex gap-4 items-center">
                <Clock className="text-[#D4AF37]" size={18} />
                <span>Open Daily · 10 AM – 9 PM</span>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={BUSINESS.mapShortLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-directions"
                className="btn-primary"
              >
                Get Directions <ArrowRight size={16} />
              </a>
              <a
                href={waLink("Hello Faisal Car Bazar! I'd like to visit your showroom.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-whatsapp"
                className="btn-secondary"
              >
                WhatsApp Us
              </a>
            </div>
          </div>
          <div className="map-frame h-[420px]">
            <iframe
              title="Faisal Car Bazar Location"
              src={BUSINESS.mapEmbed}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
